import React from "react"
import { Shield, Lock, Search, Terminal, Globe } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useScrollAnimation } from "../hooks/useScrollAnimation"

export default function ComplianceSection() {
    const { t } = useTranslation()
    const ref = useScrollAnimation()

    const items = [
        {
            icon: <Lock className="w-6 h-6 text-emerald-600" />,
            title: t('compliance.point1_title'),
            text: t('compliance.point1_desc')
        },
        {
            icon: <Search className="w-6 h-6 text-emerald-600" />,
            title: t('compliance.point2_title'),
            text: t('compliance.point2_desc')
        },
        {
            icon: <Terminal className="w-6 h-6 text-emerald-600" />,
            title: t('compliance.point3_title'),
            text: t('compliance.point3_desc')
        },
        {
            icon: <Globe className="w-6 h-6 text-emerald-600" />,
            title: t('compliance.point4_title'),
            text: t('compliance.point4_desc')
        }
    ]

    return (
        <section id="compliance" ref={ref} className="scroll-animate py-24 bg-white relative overflow-hidden">
            <div className="container mx-auto px-4 md:px-6">
                <div className="max-w-5xl mx-auto">
                    <div className="bg-slate-50 rounded-[3rem] p-8 md:p-16 border border-slate-100 shadow-inner relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl -mr-32 -mt-32"></div>

                        <div className="text-center mb-12 relative z-10">
                            <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-5xl mb-6">
                                {t('compliance.title')}
                            </h2>
                            <p className="text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed">
                                {t('compliance.description')}
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-8 relative z-10">
                            {items.map((item, index) => (
                                <div key={index} className="flex flex-col gap-4 p-8 bg-white rounded-3xl shadow-sm border border-slate-100 hover:border-emerald-500/30 hover:shadow-xl transition-all duration-500 group">
                                    <div className="flex items-center gap-4">
                                        <div className="shrink-0 p-3 bg-emerald-50 rounded-2xl group-hover:bg-emerald-500 transition-colors duration-300">
                                            {React.cloneElement(item.icon as React.ReactElement<{ className?: string }>, { className: "w-6 h-6 text-emerald-600 group-hover:text-white transition-colors duration-300" })}
                                        </div>
                                        <h3 className="text-xl font-bold text-slate-900">{item.title}</h3>
                                    </div>
                                    <p className="text-slate-600 leading-relaxed">
                                        {item.text}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Commitment & Model Section */}
                    <div className="mt-16 bg-slate-900 rounded-[2.5rem] p-8 md:p-12 shadow-2xl relative overflow-hidden group">
                        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/10 to-transparent"></div>
                        <div className="flex flex-col lg:flex-row items-center gap-10 relative z-10">
                            <div className="shrink-0 w-20 h-20 bg-emerald-500 rounded-3xl flex items-center justify-center shadow-[0_0_30px_rgba(16,185,129,0.4)] group-hover:scale-110 transition-transform duration-500">
                                <Shield className="w-10 h-10 text-slate-950" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
                                    {t('compliance.disclaimerTitle')}
                                </h3>
                                <div className="grid sm:grid-cols-1 gap-4 text-slate-300">
                                    {[t('compliance.disclaimer1'), t('compliance.disclaimer2'), t('compliance.disclaimer3')].map((text, i) => (
                                        <div key={i} className="flex items-start gap-3">
                                            <div className="mt-2 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0"></div>
                                            <p className="text-lg leading-relaxed">{text}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
